import React from "react";
import { Outlet, useNavigate, NavLink } from "react-router-dom";
import { LogOut, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import LegalGate from "@/components/LegalGate";
import LegalFooter from "@/components/LegalFooter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PORTAL_NAV = [
  { to: "/portal", label: "Home", end: true },
  { to: "/portal/buildings", label: "Buildings" },
  { to: "/portal/maintenance", label: "Maintenance" },
  { to: "/portal/quotes", label: "Quotes" },
  { to: "/portal/tenders", label: "Contractors" },
  { to: "/portal/invoices", label: "Invoices" },
  { to: "/portal/request-inspection", label: "Request" },
];

export default function PortalLayout() {
  const { user, logout, impersonating, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => {
    logout();
    navigate("/portal/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <LegalGate />
      {impersonating && (
        <div className="sticky top-0 z-50 flex items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-sm font-semibold text-amber-950" data-testid="portal-impersonation-banner">
          <span>Previewing the portal as {user?.name || impersonating.label} — read-only.</span>
          <button onClick={exitImpersonation} className="rounded-full bg-amber-950/10 px-3 py-0.5 text-xs font-bold hover:bg-amber-950/20" data-testid="portal-exit-impersonation-btn">
            Exit preview
          </button>
        </div>
      )}
      <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 lg:px-8">
        <div className="flex items-center gap-3">
          <img src="/fdcheck-logo.jpg" alt="FD Check" className="h-9 w-auto object-contain" />
          <span className="hidden rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500 sm:inline">Client Portal</span>
        </div>
        <nav className="flex items-center gap-1">
          {PORTAL_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`portal-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2 px-2" data-testid="portal-user-menu">
              <span className="hidden text-sm font-semibold text-slate-700 sm:block">{user?.name}</span>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="font-semibold">{user?.name}</div>
              <div className="text-xs font-normal text-slate-500">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/portal/report-failure")} data-testid="portal-menu-report">Report a fault</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/portal/upload-report")} data-testid="portal-menu-upload">Upload a report</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/portal/account")} data-testid="portal-menu-account">My account</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} data-testid="portal-logout-btn" className="text-red-600 focus:text-red-600">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>
      <main className="px-4 py-6 lg:px-8 lg:py-8">
        <Outlet />
      </main>
      <LegalFooter className="pb-8 pt-2" />
    </div>
  );
}
